import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-modal';
import { RepairCodeExclusionService } from '../../../services/repair-code-exclusion.service';
import { RepairCodeExclusionHeader } from '../../../models/repair-code-exclusion.model';
import { RepairCodeExclusionFilterForm } from '../../../form-model/repair-code-exclusion-filter.form.model';
import { RepairCodeExclusionFilter } from '../../../models/repair-code-exclusion-filter.model';
import { RepairCodeExclusionSearchTextForm } from '../../../form-model/repair-code-exclusion-search-text.form.model';
import { RepairCodeExclusionListGridColumns } from './repair-code-exclusion-grid.config';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ToastrService } from '../../../services/toastr-service';
import { PaginationComponent } from '../../../shared-components/aems-pagination/aems-pagination.component';
import { readNonEmptyStringArray, toSortExpression } from '../../../helper/list-grid-helpers';
import { KeyedListGridBase } from '../../../helper/keyed-list-grid.base';

@Component({
  selector: 'app-repair-code-exclusion-grid',
  imports: [CommonModule, RouterModule, NgxSkeletonLoaderModule, LoaderComponent, PaginationComponent],
  templateUrl: './repair-code-exclusion-grid.component.html',
  styleUrl: './repair-code-exclusion-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairCodeExclusionGridComponent
  extends KeyedListGridBase<RepairCodeExclusionHeader, RepairCodeExclusionFilter>
  implements OnInit, AfterViewInit, OnDestroy
{
  openDeleteConfirmation = false;
  selectedRow: RepairCodeExclusionHeader | null = null;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    filterForm: FormGroup<RepairCodeExclusionFilterForm>;
    searchTextForm: FormGroup<RepairCodeExclusionSearchTextForm>;
  }>;
  columns = RepairCodeExclusionListGridColumns;

  constructor(
    private readonly service: RepairCodeExclusionService,
    router: Router,
    private readonly toastrService: ToastrService,
    public authPolicy: AuthPolicies,
  ) {
    super(router, {});
  }

  protected get basePath(): string {
    return '/master-data/repair-code-exclusion';
  }

  protected loadPage(page: number, limit: number, sort: string | undefined, filters: RepairCodeExclusionFilter) {
    return this.service.getList(page, limit, sort, filters);
  }

  ngOnInit(): void {
    this.initializeList();
  }

  ngAfterViewInit(): void {
    this.subscribeToFilterTriggers(this.filterFormGroup.controls.filterForm, this.filterFormGroup.controls.searchTextForm);
  }

  ngOnDestroy(): void {
    this.destroyList();
  }

  onDelete(row: RepairCodeExclusionHeader): void {
    this.selectedRow = row;
    this.openDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.closeDeleteDialog();
  }

  confirmDelete(): void {
    if (!this.selectedRow) {
      this.closeDeleteDialog();
      return;
    }
    const row = this.selectedRow;
    this.service
      .delete(row.repairMode, row.repairCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.getList();
          this.toastrService.showToastr(`Repair code ${row.repairCode} mode ${row.repairMode} exclusion deleted successfully`, 'success');
        },
        error: () => {
          this.closeDeleteDialog();
          this.toastrService.showToastr('Failed to delete exclusion', 'error');
        },
      });
  }

  private closeDeleteDialog(): void {
    this.openDeleteConfirmation = false;
    this.selectedRow = null;
  }

  protected applyFilters(): void {
    if (this.filterFormGroup.controls.filterForm.valid) {
      this.filterParam = this.extractFilterParams();
      this.currentPage = 1;
      this.getList();
    }
  }

  private extractFilterParams(): RepairCodeExclusionFilter {
    const form = this.filterFormGroup.controls.filterForm;
    const filters: RepairCodeExclusionFilter = {};

    const repairCodes = readNonEmptyStringArray(form, 'RepairCodeDescriptionList');
    const repairModes = readNonEmptyStringArray(form, 'RepairModeList');
    if (repairCodes) filters.RepairCodeDescriptionList = repairCodes;
    if (repairModes) filters.RepairModeList = repairModes;

    const searchText = this.filterFormGroup.controls.searchTextForm.get('SearchText')?.value;
    if (searchText) {
      filters.SearchText = searchText;
    }

    return filters;
  }

  clearFilter(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.searchTextForm.get('appliedSearchTextFilter')?.setValue(true);
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
  }

  override onSortDirectionChange(event: Event): void {
    const sort = toSortExpression(event, 'id');
    if (!sort) return;

    this.sort = sort;
    this.currentPage = 1;
    this.getList();
  }

  override onPageLimitChanged(newLimit: number): void {
    this.currentPage = 1;
    this.pageLimit = newLimit;
    this.getList();
  }

  override onPageChanged(newPage: number): void {
    this.currentPage = newPage;
    this.getList();
  }

  getList(): void {
    this.reload();
  }
}
